// ===== CÁLCULOS DE ENVÍO =====

/**
 * Calcula el costo de envío basado en provincia y tipo de productos
 * @param {string} provinceLabel - Nombre de la provincia
 * @param {Array} cartItems - Items del carrito
 * @param {number} shippingCost - Costo de envío normal
 * @param {number} shippingCostIslands - Costo de envío para islas
 * @returns {number} Costo de envío
 */
export const calculateShippingFee = (provinceLabel, cartItems, shippingCost = 3.8, shippingCostIslands = 12) => {
  const normalizedProvince =
    typeof provinceLabel === 'string'
      ? provinceLabel.toLowerCase().trim()
      : provinceLabel?.label?.toLowerCase?.() ||
        provinceLabel?.value?.toString?.().toLowerCase?.() ||
        '';
  const expensiveProvinces = [
    'baleares',
    'illes balears',
    'canarias',
    'santa cruz de teneri',
    'santa cruz de tenerife',
    'las palmas'
  ];
  const hasBooks = cartItems.some((item) => ((item.type === 'book') || (item.type === undefined)));
  
  if (!hasBooks) return 0;
  return expensiveProvinces.includes(normalizedProvince) ? shippingCostIslands : shippingCost;
};

/**
 * Calcula el costo de envío final considerando envío gratuito
 * @param {number} shippingCost - Costo base de envío
 * @param {number} minPurchaseForFreeShipping - Mínimo para envío gratuito
 * @param {number} cartTotal - Total del carrito
 * @param {boolean} isConcourseUser - Si el cliente es concurso
 * @returns {number} Costo final de envío
 */
export const calculateShippingCosts = (shippingCost, minPurchaseForFreeShipping, cartTotal, isConcourseUser = false) => {
  // Si es cliente concurso, el shipping cost debe ser 0
  if (isConcourseUser) {
    return 0;
  }
  if (cartTotal >= minPurchaseForFreeShipping) {
    return 0;
  }
  return shippingCost;
};

// ===== CÁLCULOS DE PRECIOS =====

/**
 * Calcula el precio base (sin IVA) de un producto
 * @param {number} price - Precio con IVA
 * @param {number} iva - Porcentaje de IVA
 * @returns {number} Precio base
 */
export const calculateBasePrice = (price, iva) => {
  const pvp = Number(parseFloat(isNaN(price) ? 0.0 : price).toFixed(4));
  return Number(((pvp * 100) / (100 + iva)).toFixed(4));
};

/**
 * Calcula el IVA de un producto
 * @param {number} price - Precio con IVA
 * @param {number} basePrice - Precio base
 * @returns {number} IVA
 */
export const calculateIva = (price, basePrice) => {
  const pvp = Number(parseFloat(isNaN(price) ? 0.0 : price).toFixed(4));
  return Number((pvp - basePrice).toFixed(4));
};

/**
 * Recalcula todos los totales del carrito
 * @param {Array} items - Items del carrito
 * @param {number} shippingCosts - Costo de envío
 * @param {Object} giftCard - Tarjeta regalo (opcional)
 * @returns {Object} Totales calculados
 */
export const calculateCartTotals = (items, shippingCosts = 0, giftCard = null) => {
  let subtotal = 0;
  let iva = 0;
  let total = 0;
  let countItems = 0;

  // Calcular totales de productos (IVA incluido en el precio)
  items.forEach(item => {
    const unitPrice = Number(item.unit_price ?? item.price) || 0;
    const basePrice = calculateBasePrice(unitPrice, item.iva || 4);
    const itemIva = calculateIva(unitPrice, basePrice);
    
    subtotal += basePrice * item.quantity;
    iva += itemIva * item.quantity;
    total += unitPrice * item.quantity;
    countItems += item.quantity;
  });

  // Manejar tarjeta regalo si existe
  let finalSubtotal = subtotal;
  let finalIva = iva;
  let finalTotal = total;
  let finalCountItems = countItems;

  if (giftCard && giftCard.discount_amount > 0) {
    const giftCardAmount = giftCard.discount_amount;
    const giftCardIva = giftCardAmount * 0.04; // 4% de IVA sobre la tarjeta regalo
    
    // Agregar el amount de la tarjeta regalo al subtotal
    finalSubtotal = Number((subtotal + giftCardAmount).toFixed(4));
    
    // Agregar el 4% del amount de la tarjeta regalo al IVA
    finalIva = Number((iva + giftCardIva).toFixed(4));
    
    // El total final es subtotal + IVA (que incluye el amount de la tarjeta regalo)
    finalTotal = Number((finalSubtotal + finalIva).toFixed(4));
    
    // Contar la tarjeta regalo como un item adicional
    finalCountItems = countItems + 1;
  }

  return {
    subtotal: finalSubtotal,
    iva: finalIva,
    total: finalTotal,
    countItems: finalCountItems,
    totalWithShipping: Number((finalTotal + shippingCosts).toFixed(4))
  };
};

// ===== CÁLCULOS DE DESCUENTOS =====

/**
 * Calcula el monto de descuento
 * @param {number} subtotal - Subtotal
 * @param {number} iva - IVA
 * @param {number} percentageDiscount - Porcentaje de descuento
 * @returns {number} Monto del descuento
 */
export const calculateDiscountAmount = (subtotal, iva, percentageDiscount) => {
  return Number(((subtotal + iva) * percentageDiscount / 100).toFixed(2));
};

/**
 * Calcula el total con descuento
 * @param {number} subtotal - Subtotal
 * @param {number} iva - IVA
 * @param {number} amountDiscount - Monto del descuento
 * @param {number} shippingCosts - Costo de envío
 * @returns {number} Total con descuento
 */
export const calculateTotalOrderWithDiscount = (subtotal, iva, amountDiscount, shippingCosts) => {
  return Number(((subtotal + iva) - amountDiscount + shippingCosts).toFixed(2));
};

/**
 * Calcula el total del resumen de la orden
 * @param {Object} discountCart - Configuración de descuento
 * @param {number} total - Total base
 * @param {number} shippingCosts - Costo de envío
 * @returns {number} Total del resumen
 */
export const calculateTotalOrderSummary = (discountCart, total, shippingCosts) => {
  if (discountCart?.type === 'percentage') {
    return Number(total.toFixed(2));
  } else {
    return Number((total + shippingCosts).toFixed(2));
  }
};
